import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, IconButton, useTheme, alpha, Chip, Tooltip } from '@mui/material';
import { ChevronRight, Folder, FolderOpen, MoreVertical, FileText, Edit } from 'lucide-react';
import { ProjectHierarchy, GetProjectResponse } from '../../api/protosCompiled/project/project_pb';

// Component to handle text overflow detection for project names
const ProjectNameWithTooltip: React.FC<{
  projectName: string;
  theme: any;
  project: GetProjectResponse.AsObject;
  onProjectEdit?: (project: GetProjectResponse.AsObject) => void;
}> = ({ projectName, theme, project, onProjectEdit }) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const element = textRef.current;
        setIsOverflowing(element.scrollWidth > element.clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [projectName]);

  const typographyElement = (
    <Typography
      ref={textRef}
      variant="body2"
      sx={{
        color: theme.palette.text.secondary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        fontSize: '0.875rem',
        flex: 1,
      }}
    >
      {projectName}
    </Typography>
  );

  return isOverflowing ? (
    <Tooltip
      title={projectName}
      placement="top"
      enterDelay={300}
      leaveDelay={50}
      arrow
      PopperProps={{
        style: { pointerEvents: 'none' },
      }}
    >
      {typographyElement}
    </Tooltip>
  ) : (
    typographyElement
  );
};

interface ProjectHierarchyNodeProps {
  node: ProjectHierarchy.AsObject;
  level: number;
  isExpanded: boolean;
  isSelected: boolean;
  onToggleExpand: (nodeId: string) => void;
  onSelectNode: (nodeId: string) => void;
  hasChildren: boolean;
  onContextMenu?: (event: React.MouseEvent, node: ProjectHierarchy.AsObject) => void;
  onEllipsisClick?: (event: React.MouseEvent, node: ProjectHierarchy.AsObject) => void;
  onDragStart?: (event: React.DragEvent, node: ProjectHierarchy.AsObject) => void;
  onDragOver?: (event: React.DragEvent, node: ProjectHierarchy.AsObject) => void;
  onDrop?: (event: React.DragEvent, node: ProjectHierarchy.AsObject) => void;
  isDragOver?: boolean;
  isDragging?: boolean;
  projects?: GetProjectResponse.AsObject[];
  onProjectClick?: (project: GetProjectResponse.AsObject) => void;
  onProjectEdit?: (project: GetProjectResponse.AsObject) => void;
}

export const ProjectHierarchyNode: React.FC<ProjectHierarchyNodeProps> = ({
  node,
  level,
  isExpanded,
  isSelected,
  onToggleExpand,
  onSelectNode,
  hasChildren,
  onContextMenu,
  onEllipsisClick,
  onDragStart,
  onDragOver,
  onDrop,
  isDragOver = false,
  isDragging = false,
  projects = [],
  onProjectClick,
  onProjectEdit,
}) => {
  const theme = useTheme();

  const handleClick = () => {
    if (hasChildren) {
      onToggleExpand(node.id);
    }
    onSelectNode(node.id);
  };

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (onContextMenu) {
      onContextMenu(event, node);
    }
  };

  const handleEllipsisClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (onEllipsisClick) {
      onEllipsisClick(event, node);
    }
  };

  const handleDragStart = (event: React.DragEvent) => {
    if (onDragStart) {
      onDragStart(event, node);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (onDragOver) {
      onDragOver(event, node);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    if (onDrop) {
      onDrop(event, node);
    }
  };

  const paddingLeft = level * 12; // 24px per level
  const projectCount = projects.length;

  const handleProjectClick = (project: GetProjectResponse.AsObject, event: React.MouseEvent) => {
    event.stopPropagation();
    if (onProjectClick) {
      onProjectClick(project);
    }
  };

  const handleProjectEdit = (project: GetProjectResponse.AsObject, event: React.MouseEvent) => {
    event.stopPropagation();
    if (onProjectEdit) {
      onProjectEdit(project);
    }
  };

  return (
    <Box>
      {/* Folder Node */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 0.5,
          px: 1,
          pl: `${paddingLeft + 8}px`, // Apply indentation to entire row
          cursor: 'pointer',
          borderRadius: 1,
          transition: 'all 0.2s',
          backgroundColor: isSelected
            ? alpha(theme.palette.primary.main, 0.1)
            : isDragOver
              ? alpha(theme.palette.primary.main, 0.15)
              : 'transparent',
          opacity: isDragging ? 0.5 : 1,
          border: isDragOver ? `2px dashed ${theme.palette.primary.main}` : '2px solid transparent',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
          },
        }}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        draggable
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Expand/Collapse Icon */}
        <Box sx={{ width: 24, display: 'flex', justifyContent: 'center', mr: 1 }}>
          {hasChildren ? (
            <IconButton
              size="small"
              sx={{
                p: 0,
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
                color: theme.palette.text.secondary,
              }}
              onClick={e => {
                e.stopPropagation();
                onToggleExpand(node.id);
              }}
            >
              <ChevronRight size={16} />
            </IconButton>
          ) : (
            <Box sx={{ width: 16, height: 16 }} />
          )}
        </Box>

        {/* Folder Icon */}
        <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
          {isExpanded && hasChildren ? (
            <FolderOpen size={16} color={theme.palette.text.secondary} />
          ) : (
            <Folder size={16} color={theme.palette.text.secondary} />
          )}
        </Box>

        {/* Node Content */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: isSelected ? 600 : 400,
              color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {node.name}
          </Typography>
          {projectCount > 0 && (
            <Chip
              size="small"
              label={projectCount}
              sx={{
                height: 20,
                fontSize: '0.75rem',
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                '& .MuiChip-label': {
                  px: 1,
                },
              }}
            />
          )}
        </Box>

        {/* Ellipsis Button */}
        {onEllipsisClick && (
          <IconButton
            size="small"
            sx={{
              p: 0.5,
              opacity: 0,
              transition: 'opacity 0.2s',
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              },
            }}
            onClick={handleEllipsisClick}
          >
            <MoreVertical size={16} />
          </IconButton>
        )}
      </Box>

      {/* Projects List - Only show when expanded and has projects */}
      {isExpanded && projects.length > 0 && (
        <Box sx={{ pl: `${paddingLeft + 32}px` }}>
          {projects.map(project => (
            <Box
              key={project.project!.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                py: 0.5,
                px: 1,
                cursor: 'pointer',
                borderRadius: 1,
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  '& .project-edit-button': {
                    opacity: 1,
                  },
                },
              }}
              onClick={e => handleProjectClick(project, e)}
            >
              {/* Project Icon */}
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                <FileText
                  size={14}
                  color={
                    project.project?.projectcontractsList &&
                    project.project.projectcontractsList.length > 0
                      ? theme.palette.success.main
                      : theme.palette.text.secondary
                  }
                />
              </Box>

              {/* Project Name */}
              <ProjectNameWithTooltip
                projectName={project.project!.name}
                theme={theme}
                project={project}
                onProjectEdit={onProjectEdit}
              />

              {/* Edit Button - Only show on hover */}
              {onProjectEdit && (
                <IconButton
                  className="project-edit-button"
                  size="small"
                  sx={{
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    p: 0.5,
                    ml: 1,
                    color: theme.palette.text.secondary,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                    },
                  }}
                  onClick={e => handleProjectEdit(project, e)}
                >
                  <Edit size={12} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
